import { useState, useEffect } from 'react'
import { duplicateTheme, type SkippedTemplate } from '@/api/admin'
import { getErrorMessage } from '@/utils/global/getErrorMessage'
import { LaunchCopyOutcome } from './LaunchCopyOutcome'
import { useSetToast } from '@/hooks'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import { Dropdown, FullscreenModal } from '@/components/global'
import { Button, Input } from '@/components/priority'
import { getThemeCategories } from '@/api/themes'

type ShowState = [boolean, (value: boolean) => void]

type Theme = {
  id: string
  name: string
}

type ThemeDuplicateModalProps = {
  show: ShowState
}

export const ThemeDuplicateModal = ({ show }: ThemeDuplicateModalProps) => {
  const close = show[1]
  const [themes, setThemes] = useState<Theme[]>([])
  const [selectedTheme, setSelectedTheme] = useState('')
  const [newThemeName, setNewThemeName] = useState('')
  const [loading, setLoading] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [skipped, setSkipped] = useState<SkippedTemplate[]>([])
  const setToast = useSetToast()

  const resetState = () => {
    setSelectedTheme('')
    setNewThemeName('')
    setRefusal(null)
    setSkipped([])
    close(false)
  }

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await getThemeCategories()
        const sortedThemes = response.data[0].themes.sort(
          (a: Theme, b: Theme) => a.name.localeCompare(b.name),
        )
        setThemes(sortedThemes)
      } catch (error) {
        console.error('Failed to fetch themes:', error)
      }
    }

    fetchThemes()
  }, [])

  const handleSubmit = async () => {
    if (!selectedTheme || !newThemeName.trim()) return

    setLoading(true)
    setRefusal(null)
    setSkipped([])
    try {
      // Get the selected theme to copy its properties
      const themeToCopy = themes.find((theme) => theme.id === selectedTheme)
      if (!themeToCopy) {
        throw new Error('Selected theme not found')
      }

      const response = await duplicateTheme({
        baseThemeId: selectedTheme,
        newThemeName: newThemeName.trim(),
      })

      setToast({
        text: 'Theme duplicated successfully!',
        icon: 'Check',
      })
      const left = response.data.skipped ?? []
      if (left.length > 0) {
        // The theme now exists; clearing the choices keeps another click
        // from creating a second one while the list stays on screen.
        setSelectedTheme('')
        setNewThemeName('')
        setSkipped(left)
        return
      }
      resetState()
    } catch (error) {
      setRefusal(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    resetState()
  }
  return (
    // @ts-expect-error N/A
    <FullscreenModal show={show} hideClose>
      <Flex
        column
        padding="32px"
        width="500px"
        height="auto"
        justify="space-between"
      >
        <Flex column>
          <Text size="24px" weight="600" margin="0 0 20px 0">
            Copy Theme
          </Text>
          <Text margin="0 0 20px 0">
            Select a theme to copy, then enter a name for the new theme.
          </Text>
          <Dropdown
            width="100%"
            margin="0 0 24px 0"
            color="inherit"
            // @ts-expect-error N/A
            selected={[selectedTheme, setSelectedTheme]}
            data-testid="theme-duplicate-base"
            placeholder="Select a theme to copy"
            items={themes}
            valueKey="id"
            textKey="name"
            full={true}
          />
          <Flex column width="100%" margin="0 0 24px 0">
            <Input
              // @ts-expect-error N/A
              width="100%"
              margin="0 0 24px 0"
              label="New Theme Name"
              value={[newThemeName, setNewThemeName]}
              placeholder="Enter name for the new theme"
              data-testid="theme-duplicate-name"
            />
          </Flex>
          <LaunchCopyOutcome refusal={refusal} skipped={skipped} />
        </Flex>
        <Flex justify="flex-end" margin="24px 0 0 0">
          <Button margin="0 16px 0 0" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            margin="0 12px 0 0"
            primary
            onClick={handleSubmit}
            data-testid="theme-duplicate-submit"
          >
            {loading ? 'Copying...' : 'Copy Theme'}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}

export default ThemeDuplicateModal
